/**
 * Клиентское зеркало правил обучения (api-contract §3e / canon service-training). Чистые функции —
 * тестируются изолированно (см. training.test.ts). Те же правила сервер применяет авторитетно;
 * здесь — для бейджей заявки, журнала сроков и блока «Обучения» в карточке сотрудника.
 */
import type { StatusRole } from '@/design-system/tokens/status'
import type { DotVariant } from '@/design-system/components/primitives/status-dot'
import type {
  ListenerField,
  ListenerStatus,
  RecurrenceMode,
  TrainingDirection,
  TrainingStatus,
} from '@/lib/api'
import { isValidEmail, isValidSnils } from '@/lib/validation'

// --- Ярлыки направлений (§3e) ---

/** Русские ярлыки направлений (дословно по канону service-training). */
export const TRAINING_DIRECTION_LABELS: Record<TrainingDirection, string> = {
  ot: 'Охрана труда',
  fire: 'Пожарная безопасность',
  ecology: 'Экология',
  work_skills: 'Рабочие специальности',
  industrial_safety: 'Промбезопасность',
  other: 'Прочее',
}

// `directionLabel()` и список `TRAINING_DIRECTIONS` удалены (centrika-docs!55): направления —
// редактируемый справочник, имена берутся с сервера (directionName), а не из статичных констант.
// `TRAINING_DIRECTION_LABELS` остаётся дефолтными именами системных направлений для journal-mirror
// (columnsForProfile/buildEmployeeBadges).

// --- Статусы (§3e) ---

export interface TrainingStatusView {
  label: string
  role: StatusRole
}

/**
 * Ярлык + цветовая роль статуса записи/строки (§3e). Отображаемая метка `valid` = «В силе»;
 * `in_process` — серый (neutral), НЕ синий: §3e «in_process/нет данных→gray».
 */
export const TRAINING_STATUS_VIEW: Record<TrainingStatus, TrainingStatusView> = {
  valid: { label: 'В силе', role: 'success' },
  in_process: { label: 'В процессе', role: 'neutral' },
  expiring: { label: 'Истекает скоро', role: 'warning' },
  expired: { label: 'Просрочено', role: 'danger' },
}

/** Статус → вариант светофор-точки/чипа (§3e): valid→green, expiring→amber, expired→red, in_process→gray. */
export function trainingStatusToVariant(status: TrainingStatus): DotVariant {
  switch (status) {
    case 'valid':
      return 'green'
    case 'expiring':
      return 'amber'
    case 'expired':
      return 'red'
    case 'in_process':
      return 'gray'
  }
}

// --- Расчёт срока и статуса (зеркало серверного, §3e) ---

const MS_PER_DAY = 24 * 60 * 60 * 1000

/** Дата-только (локальная полночь) из строки YYYY-MM-DD — устойчиво к таймзонам. */
function parseDateOnly(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return null
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** Срок следующего обучения = дата последнего обучения + периодичность (мес.); YYYY-MM-DD. */
export function computeDueOn(lastTrainedOn: string, recurrenceMonths: number): string | null {
  const base = parseDateOnly(lastTrainedOn)
  if (!base) return null
  const due = new Date(base.getFullYear(), base.getMonth() + recurrenceMonths, base.getDate())
  return `${due.getFullYear()}-${pad2(due.getMonth() + 1)}-${pad2(due.getDate())}`
}

export interface TrainingStatusInput {
  lastTrainedOn?: string | null
  /** Периодичность в месяцах; null — «без срока». */
  recurrenceMonths: number | null
}

/**
 * Статус записи обучения (§3e, сервер авторитетно — здесь зеркало для согласованности и тестов):
 * нет даты → `in_process`; «без срока» с датой → `valid`; иначе по сроку — `expired`/`expiring`/`valid`.
 */
export function computeTrainingStatus(
  input: TrainingStatusInput,
  thresholdDays = 90,
  now: Date = new Date(),
): TrainingStatus {
  if (!input.lastTrainedOn) return 'in_process'
  if (input.recurrenceMonths == null) return 'valid'
  const due = computeDueOn(input.lastTrainedOn, input.recurrenceMonths)
  const dueDate = due ? parseDateOnly(due) : null
  if (!dueDate) return 'in_process'
  const today = startOfDay(now).getTime()
  const dueMs = dueDate.getTime()
  if (dueMs < today) return 'expired'
  if (dueMs <= today + thresholdDays * MS_PER_DAY) return 'expiring'
  return 'valid'
}

/**
 * Короткий срок для клетки журнала / записи (§3e): «просроч.» (expired); «N дн.» (expiring);
 * «до MM.YY» (valid со сроком); «Определяет работодатель» (режим employer, centrika-docs!54);
 * «Бессрочно» (valid без срока, F2-16); «—» (in_process). Зеркало серверного termForStatus.
 */
export function shortTerm(
  row: { status: TrainingStatus; dueOn?: string | null; recurrenceMode?: RecurrenceMode },
  now: Date = new Date(),
): string {
  if (row.status === 'expired') return 'просроч.'
  if (row.status === 'in_process') return '—'
  // centrika-docs!54: программа режима «определяет работодатель» — срок не рассчитывается.
  if (row.recurrenceMode === 'employer') return 'Определяет работодатель'
  // F2-16: valid без даты окончания → «Бессрочно» (не «—»), как в серверном termForStatus.
  if (!row.dueOn) return row.status === 'valid' ? 'Бессрочно' : '—'
  const due = parseDateOnly(row.dueOn)
  if (!due) return row.status === 'valid' ? 'Бессрочно' : '—'
  if (row.status === 'expiring') {
    const days = Math.max(0, Math.ceil((due.getTime() - startOfDay(now).getTime()) / MS_PER_DAY))
    return `${days} дн.`
  }
  return `до ${pad2(due.getMonth() + 1)}.${String(due.getFullYear()).slice(-2)}`
}

// --- ОТ-индексы (§3e) ---

/** Канонический набор/порядок индексов охраны труда (только для direction='ot'). */
export const OT_INDICES = ['А', 'Б', 'СИЗ', 'ПП', 'В'] as const
export type OtIndex = (typeof OT_INDICES)[number]

export function isOtIndex(value: string): value is OtIndex {
  return (OT_INDICES as readonly string[]).includes(value)
}

/** Индексы в каноническом порядке через разделитель (для чипа-сводки), напр. «А · Б · СИЗ». */
export function formatOtIndices(indices: string[]): string {
  return OT_INDICES.filter((i) => indices.includes(i)).join(' · ')
}

// --- Колонки журнала по профилю (§3e) ---

/** «Тяжёлые» профили — получают отраслевые колонки (производство/сельхоз/ЖКХ). */
export const HEAVY_PROFILES = ['manufacturing', 'agriculture', 'utilities']

export interface JournalColumn {
  key: TrainingDirection
  label: string
}

/**
 * Колонки журнала по профилю клиента (§3e). Базовые всегда: ot/fire/ecology. Для «тяжёлых» профилей
 * добавляется `work_skills`; `industrial_safety` — только для «тяжёлого» профиля при флаге «есть ОПО».
 * Вне набора профилей — только базовые. Зеркало серверного `columns`.
 */
export function columnsForProfile(
  industryProfile: string | undefined | null,
  hasOpo: boolean,
): JournalColumn[] {
  const cols: JournalColumn[] = [
    { key: 'ot', label: TRAINING_DIRECTION_LABELS.ot },
    { key: 'fire', label: TRAINING_DIRECTION_LABELS.fire },
    { key: 'ecology', label: TRAINING_DIRECTION_LABELS.ecology },
  ]
  const heavy = !!industryProfile && HEAVY_PROFILES.includes(industryProfile)
  if (heavy) {
    cols.push({ key: 'work_skills', label: TRAINING_DIRECTION_LABELS.work_skills })
    if (hasOpo)
      cols.push({ key: 'industrial_safety', label: TRAINING_DIRECTION_LABELS.industrial_safety })
  }
  return cols
}

// --- Бейджи сотрудника (§3e: «Экология», «Охрана труда», «Всё в силе») ---

export interface EmployeeBadge {
  label: string
  status: TrainingStatus
}

/** Худший из двух «проблемных» статусов (expired > expiring). */
function worseProblem(a: TrainingStatus, b: TrainingStatus): TrainingStatus {
  return a === 'expired' || b === 'expired' ? 'expired' : 'expiring'
}

/**
 * Бейджи сотрудника по записям обучения (§3e): что просрочено/истекает (по направлению, худший
 * статус), либо единственный бейдж «Всё в силе», если проблем нет. Зеркало серверного `badges`.
 */
export function buildEmployeeBadges(
  records: { direction: TrainingDirection; status: TrainingStatus }[],
): EmployeeBadge[] {
  const byDirection = new Map<TrainingDirection, TrainingStatus>()
  for (const r of records) {
    if (r.status !== 'expired' && r.status !== 'expiring') continue
    const prev = byDirection.get(r.direction)
    byDirection.set(r.direction, prev ? worseProblem(prev, r.status) : r.status)
  }
  if (byDirection.size === 0) return [{ label: 'Всё в силе', status: 'valid' }]
  return Array.from(byDirection.entries()).map(([direction, status]) => ({
    label: TRAINING_DIRECTION_LABELS[direction],
    status,
  }))
}

// --- Мастер обучения клиента (p2-service-training, §6.1-6.5) ---

/**
 * Форматы согласия ПД и документа об образовании (атрибут `accept` окна выбора файла): PDF или
 * фото JPG/PNG (training-documents-images — «не все умеют сканировать»). Зеркало серверного
 * `files.PersonDocExt`/`PersonDocMIME`; сервер проверяет и расширение, и содержимое файла.
 */
export const TRAINING_PERSON_DOC_ACCEPT =
  'application/pdf,.pdf,image/jpeg,.jpg,.jpeg,image/png,.png'

/** Подсказка о допустимых форматах согласия и документа об образовании. */
export const TRAINING_PERSON_DOC_FORMATS = 'PDF, JPG или PNG'

/**
 * Допустимые значения поля «Образование» на шаге подтверждения (centrika-docs!59).
 * Порядок фиксирован требованием.
 */
export const TRAINING_EDUCATION_OPTIONS = ['Высшее', 'Среднее профессиональное', 'Нет'] as const
export type TrainingEducation = (typeof TRAINING_EDUCATION_OPTIONS)[number]

/**
 * Считается ли образование сотрудника «выбранным» на шаге 3 (centrika-docs!59): значение
 * совпадает с одним из доступных вариантов. Пустое или несовпадающее — не выбрано.
 */
export function isTrainingEducationSelected(education: string | undefined): boolean {
  return TRAINING_EDUCATION_OPTIONS.includes((education ?? '') as TrainingEducation)
}

/**
 * Незаполненные поля слушателя — зеркало серверного правила полноты (domain.TrainingListenerMissing,
 * training-listener-completeness): корректный СНИЛС, дата рождения, пол, образование, e-mail,
 * согласие ПД и — если у слушателя есть программа «ВО/СПО» — документ об образовании.
 */
export function listenerMissing(e: {
  snils?: string
  birthDate?: string
  gender?: string
  education?: string
  email?: string
  hasConsent?: boolean
  needsEducationDocument?: boolean
  educationDocument?: { hasDocument?: boolean }
}): ListenerField[] {
  const miss: ListenerField[] = []
  if (!isValidSnils(e.snils ?? '')) miss.push('snils')
  if ((e.birthDate ?? '').trim() === '') miss.push('birthDate')
  if (e.gender !== 'm' && e.gender !== 'f') miss.push('gender')
  if (!isTrainingEducationSelected(e.education)) miss.push('education')
  if (!isValidEmail(e.email ?? '')) miss.push('email')
  if (!e.hasConsent) miss.push('consent')
  if (e.needsEducationDocument && !e.educationDocument?.hasDocument) miss.push('educationDocument')
  return miss
}

/** Светофор слушателя по незаполненным полям: всё / не всё / ничего. */
export function listenerStatus(e: Parameters<typeof listenerMissing>[0]): ListenerStatus {
  const missing = listenerMissing(e).length
  if (missing === 0) return 'complete'
  return missing === (e.needsEducationDocument ? 7 : 6) ? 'empty' : 'partial'
}

/** Подпись и цвет светофора слушателя (текстом — не только цветом). */
export const LISTENER_STATUS_VIEW: Record<ListenerStatus, { label: string; dot: 'green' | 'amber' | 'red' }> = {
  complete: { label: 'Заполнено', dot: 'green' },
  partial: { label: 'Частично', dot: 'amber' },
  empty: { label: 'Не заполнено', dot: 'red' },
}

/** Подписи незаполненных полей слушателя («Не заполнено: e-mail, согласие ПД»). */
export const LISTENER_FIELD_LABELS: Record<ListenerField, string> = {
  snils: 'СНИЛС',
  birthDate: 'дата рождения',
  gender: 'пол',
  education: 'образование',
  email: 'e-mail',
  consent: 'согласие ПД',
  educationDocument: 'документ об образовании',
}

/**
 * Готовы ли персональные данные сотрудника для отправки заказа обучения (шаг 3,
 * centrika-docs!86/!59): СНИЛС корректен, дата рождения заполнена, пол выбран (м/ж) и
 * образование — одно из допустимых значений. СНИЛС/дата/пол клиент правит прямо на шаге и
 * они сохраняются в карточку при отправке; образование сохраняется сразу отдельной ячейкой.
 */
export function isTrainingEmployeeReady(e: {
  snils?: string
  birthDate?: string
  gender?: string
  education?: string
}): boolean {
  return (
    isValidSnils(e.snils ?? '') &&
    (e.birthDate ?? '').trim() !== '' &&
    (e.gender === 'm' || e.gender === 'f') &&
    isTrainingEducationSelected(e.education)
  )
}

/**
 * Можно ли отправить заказ обучения: выбран ≥1 сотрудник (⇒ и ≥1 программа — в этой модели
 * состава сотрудник появляется в selectedEmployees, только будучи привязан к программе) И у
 * каждого выбранного сотрудника заполнены и корректны СНИЛС, дата рождения, пол и образование
 * (centrika-docs!86/!59; см. isTrainingEmployeeReady). Проверяется по ДЕЙСТВУЮЩИМ значениям
 * шага 3 (правки СНИЛС/даты/пола, ещё не сохранённые в карточку, учитываются вызывающей
 * стороной). Согласия ПД и документы об образовании условием отправки НЕ являются
 * (centrika-docs!36/!59): их загружают после отправки, на экране «Заказ отправлен». Зеркало
 * серверной валидации submit (handlers_training.go submitTraining) — прямой POST /submit гейт
 * не обходит.
 */
export function canSubmitTrainingWizard(wizard: {
  selectedEmployees: { snils?: string; birthDate?: string; gender?: string; education?: string }[]
}): boolean {
  return (
    wizard.selectedEmployees.length > 0 &&
    wizard.selectedEmployees.every(isTrainingEmployeeReady)
  )
}

/**
 * Подпись счётчика согласий (centrika-docs!36) — одна на экран «Заказ отправлен» и карточку
 * заказа в «Моих заказах», чтобы формулировка не разошлась между экранами. `uploaded` — число
 * сотрудников с загруженным согласием, `total` — уникальные сотрудники заказа.
 */
export function trainingConsentCounterLabel(uploaded: number, total: number): string {
  return `Согласия загружены: ${uploaded} из ${total}`
}

/**
 * Подпись счётчика документов об образовании (centrika-docs!59) — одна на компонент
 * «Документы сотрудников». `uploaded` — число сотрудников с загруженным документом,
 * `total` — уникальные сотрудники заказа.
 */
/**
 * Подпись документа об образовании (ответ заказчицы 27.10, В12). Поле — только у слушателей программ
 * с базой «ВО/СПО»: где документ не нужен, его быть не должно. Коротко «Документ о ВО/СПО» — в
 * таблицах слушателей и на шаге «Заполнение данных» (ответ заказчицы 04.11, п.11 и 12.2).
 */
export const EDUCATION_DOCUMENT_LABEL = 'Документ о ВО/СПО'

export function trainingEducationDocCounterLabel(uploaded: number, total: number): string {
  return `Документы об образовании загружены: ${uploaded} из ${total}`
}

/** Комплект согласий неполон — основание для предупреждения в карточке заказа. */
export function hasIncompleteTrainingConsents(order: {
  service: string
  dataStatus: string
  consentsUploaded: number
  consentsTotal: number
}): boolean {
  // Только ОТПРАВЛЕННЫЙ заказ обучения: «Черновик», «На заполнении» и «Требуются правки» — фаза
  // данных (orders-core, training-order-filling), в ней клиент правит анкету; отменённому
  // (order-cancel-restore) согласия не нужны. Пустой состав (`consentsTotal === 0`)
  // предупреждения не даёт — «0 из 0» ни о чём не сообщает.
  return (
    order.service === 'training' &&
    order.dataStatus !== 'draft' &&
    order.dataStatus !== 'filling' &&
    order.dataStatus !== 'changes_requested' &&
    order.dataStatus !== 'cancelled' &&
    order.consentsTotal > 0 &&
    order.consentsUploaded < order.consentsTotal
  )
}

// Группировка списка сотрудников шага 2 по СОСТОЯНИЮ обучения относительно программы
// (`programStatusGroup` / `PROGRAM_STATUS_GROUP_LABELS` / `PROGRAM_STATUS_GROUP_ORDER`) удалена:
// по centrika-docs, component.create-training-order.programs-and-employees (MR !18) диалог
// группирует сотрудников по ПОДРАЗДЕЛЕНИЯМ. Сам статус обучения по программе никуда не делся —
// он остаётся бейджем в строке сотрудника (`TRAINING_STATUS_VIEW`) и основанием массовых
// действий ниже, которые считают по `status` напрямую.

/** Массовые действия шага 2 (D7): выбрать всех в списке / просроченных / истекающих /
 * требующих внимания (все, кроме «актуальна») / снять выбор. */
export type TrainingMassActionPreset = 'all' | 'expired' | 'expiring' | 'attention' | 'none'

export const TRAINING_MASS_ACTION_LABELS: Record<TrainingMassActionPreset, string> = {
  all: 'Выбрать всех в списке',
  expired: 'Выбрать просроченных',
  expiring: 'Выбрать истекающих',
  attention: 'Выбрать требующих внимания',
  none: 'Снять выбор',
}

/**
 * Применяет массовое действие к УЖЕ отфильтрованному (поиск/подразделение/должность)
 * видимому списку — возвращает id, которые должны стать выбранными (D7, прототип `trPickPreset`).
 */
export function applyTrainingMassAction<T extends { personId: string; status: TrainingStatus }>(
  visible: T[],
  preset: TrainingMassActionPreset,
): string[] {
  switch (preset) {
    case 'all':
      return visible.map((e) => e.personId)
    case 'expired':
      return visible.filter((e) => e.status === 'expired').map((e) => e.personId)
    case 'expiring':
      return visible.filter((e) => e.status === 'expiring').map((e) => e.personId)
    case 'attention':
      return visible.filter((e) => e.status !== 'valid').map((e) => e.personId)
    case 'none':
      return []
  }
}

/**
 * Ярлык пола сотрудника. Принимает и контрактный формат (`m`/`f`), и хранилищный
 * (`male`/`female`) — старые «фотографии» состава могли быть заморожены до нормализации.
 * Общий для шага 3 мастера и состава заказа обучения (centrika-docs!41).
 */
export function genderLabel(g: string): string {
  return g === 'female' || g === 'f' ? 'женский' : g === 'male' || g === 'm' ? 'мужской' : '—'
}
